// Header: mobile menu, dropdowns, sticky / hide on scroll, transparent -> solid, search with live results.
if (!customElements.get('header-component')) {
  customElements.define('header-component', class extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.header__drawer');
      this.search = this.querySelector('[data-header-search]');
      this.menuButton = this.querySelector('[data-header-drawer-open]');
      this.dropdowns = [...this.querySelectorAll('[data-header-dropdown]')];
      // Everything registered outside this element is removed in disconnectedCallback (the editor re-renders the
      // header on every setting change).
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.addEventListener('click', (event) => this.onClick(event));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        // focus inside the dropdown that closes goes back to its toggle (it would otherwise sit on a hidden link)
        const inside = [...this.dropdowns].find((details) => details.open && details.contains(document.activeElement));
        this.closeDropdowns();
        inside?.querySelector('summary')?.focus();
      }, { signal });
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.closeDropdowns(); }, { signal });

      // Dropdowns open on hover with a mouse, on click otherwise.
      const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdowns.forEach((details) => {
        details.addEventListener('mouseenter', () => { if (hover.matches) { this.closeDropdowns(details); this.openDropdown(details); } });
        details.addEventListener('mouseleave', () => { if (hover.matches) this.closeDropdown(details); });
        details.addEventListener('toggle', () => { if (details.open) this.closeDropdowns(details); this.updateSolid(); });
      });

      [this.drawer, this.search].forEach((dialog) => {
        dialog?.addEventListener('click', (event) => { if (event.target === dialog) this.closeDialog(dialog); });
        dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.closeDialog(dialog); }); // Escape
        dialog?.addEventListener('close', () => { this.menuButton?.setAttribute('aria-expanded', 'false'); });
      });

      this.lastScroll = window.scrollY;
      this.onScroll = () => this.handleScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.handleScroll();

      document.addEventListener('cart:updated', (event) => this.updateCount(event.detail?.item_count), { signal });
      this.setupSearch();

      // Transparent: the first section slides up under the header by the header's real height (two rows with a
      // centred logo, floating gap...), set as --header-overlap on the section.
      if ('ResizeObserver' in window) {
        this.overlapObserver = new ResizeObserver(() => this.publishOverlap());
        this.overlapObserver.observe(this.closest('.shopify-section') || this);
      }
      this.publishOverlap();

      // A menu that does not fit on one line switches the header to the menu button.
      this.nav = this.querySelector('.header__nav');
      if (this.nav && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.checkFit());
        this.resizeObserver.observe(this);
        const menu = this.nav.querySelector('.header__menu');
        if (menu) this.resizeObserver.observe(menu);
        // Measure again once styles and fonts are ready (they change the menu's width).
        window.addEventListener('load', () => this.checkFit(), { once: true, signal });
        document.fonts?.ready.then(() => this.checkFit());
        this.checkFit();
      }
    }

    checkFit() {
      this.classList.remove('is-compact');
      const menu = this.nav.querySelector('.header__menu');
      if (menu && menu.scrollWidth > this.nav.clientWidth + 1) this.classList.add('is-compact');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.overlapObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    onClick(event) {
      if (event.target.closest('[data-header-drawer-open]')) this.openDialog(this.drawer, this.menuButton);
      else if (event.target.closest('[data-header-drawer-close]')) this.closeDialog(this.drawer);
      else if (event.target.closest('[data-header-search-open]')) this.openDialog(this.search);
      else if (event.target.closest('[data-header-search-close]')) this.closeDialog(this.search);
    }

    openDialog(dialog, trigger) {
      if (!dialog) return;
      this.closeDropdowns();
      // still closing (clicked again quickly): stay open instead of closing a moment later
      if (!window.theme?.motion?.reopen?.(dialog) && !dialog.open) dialog.showModal();
      trigger?.setAttribute('aria-expanded', 'true');
      dialog.querySelector('[data-header-search-input]')?.focus();
    }

    // Closing slides the drawer back out (or fades the search) instead of vanishing (assets/motion.js).
    closeDialog(dialog) {
      if (!dialog) return;
      const motion = window.theme?.motion;
      if (!motion) { dialog.close(); return; }
      const side = dialog.dataset.side === 'right' ? '100%' : '-100%';
      const frames = dialog === this.drawer ? [{ transform: 'none' }, { transform: `translateX(${side})` }] : [{ opacity: 1 }, { opacity: 0 }];
      motion.closeDialog(dialog, frames, 240);
    }

    openDropdown(details) {
      const panel = details.querySelector(':scope > .header__submenu, :scope > .header__mega');
      if (panel) window.theme?.motion?.leave(panel, [{ opacity: 1 }], 0); // stops a closing fade
      delete details.dataset.closing;
      details.open = true;
    }

    // The panel fades and slides up, then the dropdown closes; hovering back in keeps it open.
    closeDropdown(details) {
      if (!details.open || details.dataset.closing) return;
      const panel = details.querySelector(':scope > .header__submenu, :scope > .header__mega');
      const motion = window.theme?.motion;
      if (!panel || !motion || motion.off()) { details.open = false; return; }
      details.dataset.closing = 'true';
      motion.leave(panel, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-6px)' }], 160, () => {
        if (details.dataset.closing) { delete details.dataset.closing; details.open = false; }
      });
    }

    closeDropdowns(except) {
      this.dropdowns.forEach((details) => { if (details !== except) this.closeDropdown(details); });
    }

    // Sticky "hide on scroll down, show on scroll up" and transparent -> solid.
    handleScroll() {
      const y = window.scrollY;
      if (this.dataset.sticky === 'scroll_up') {
        const goingDown = y > this.lastScroll && y > this.offsetHeight * 2;
        this.classList.toggle('is-hidden', goingDown && !this.dropdowns.some((d) => d.open));
      }
      this.lastScroll = y;
      this.updateSolid();
      this.publishOffset();
    }

    // --header-offset on <html>: how far the sticky header reaches down the screen, so sticky content sits below it.
    publishOffset() {
      const sticky = this.dataset.sticky === 'always' || this.dataset.sticky === 'scroll_up';
      const bottom = sticky && !this.classList.contains('is-hidden') ? Math.max(0, Math.round(this.getBoundingClientRect().bottom)) : 0;
      if (bottom !== this.offset) {
        this.offset = bottom;
        document.documentElement.style.setProperty('--header-offset', `${bottom}px`);
      }
    }

    publishOverlap() {
      // The section's own height: it also holds the floating bar's top gap and the header's margins.
      const section = this.closest('.shopify-section');
      section?.style.setProperty('--header-overlap', `${section.offsetHeight}px`);
    }

    updateSolid() {
      const transparent = this.dataset.transparentDesktop === 'true' || this.dataset.transparentMobile === 'true';
      if (!transparent) return;
      const scrolled = this.dataset.solidOnScroll === 'true' && window.scrollY > 40;
      this.classList.toggle('is-solid', scrolled || this.dropdowns.some((d) => d.open));
    }

    updateCount(count) {
      if (typeof count !== 'number') return;
      this.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      // the link's accessible name carries the count too ("Cart (3)"), or screen readers keep the old number
      this.querySelectorAll('[data-cart-open][aria-label]').forEach((link) => link.setAttribute('aria-label', link.getAttribute('aria-label').replace(/\(\d+\)\s*$/, `(${count})`)));
    }

    // ---------- Search with results while typing (Theme settings > Search) ----------
    setupSearch() {
      const dialog = this.search;
      if (!dialog || dialog.dataset.predictive !== 'true') return;
      const input = dialog.querySelector('[data-header-search-input]');
      const results = dialog.querySelector('[data-header-search-results]');
      let timer, controller;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (!q) { results.innerHTML = ''; return; }
        timer = setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          const params = new URLSearchParams({ q, 'resources[type]': dialog.dataset.types, 'resources[limit]': dialog.dataset.limit, 'resources[options][unavailable_products]': 'last' });
          try {
            const response = await fetch(`${dialog.dataset.url}.json?${params}`, { signal: controller.signal });
            const data = await response.json();
            results.innerHTML = this.renderResults(data.resources?.results || {}, q, dialog);
          } catch (error) {
            if (error.name !== 'AbortError') results.innerHTML = '';
          }
        }, 250);
      });
    }

    renderResults(found, q, dialog) {
      const escape = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const currency = window.Shopify?.currency?.active;
      const money = (value) => { const n = Number(value); return currency && !Number.isNaN(n) ? new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(n) : escape(value); };
      const group = (title, items, render) => items?.length ? `<div><p class="header-search__group-title">${title}</p><ul class="header-search__list" role="list">${items.map(render).join('')}</ul></div>` : '';
      const products = group('Products', found.products, (p) => `<li><a class="header-search__item" href="${escape(p.url)}">${p.featured_image?.url ? `<img src="${escape(p.featured_image.url)}&width=120" alt="" loading="lazy">` : ''}<span>${escape(p.title)}${dialog.dataset.showVendor === 'true' && p.vendor ? `<span class="header-search__meta">${escape(p.vendor)}</span>` : ''}${dialog.dataset.showPrice === 'true' ? `<span class="header-search__meta header-search__price">${money(p.price)}</span>` : ''}</span></a></li>`);
      const articles = group('Blog posts', found.articles, (a) => `<li><a class="header-search__item" href="${escape(a.url)}">${escape(a.title)}</a></li>`);
      const pages = group('Pages', found.pages, (p) => `<li><a class="header-search__item" href="${escape(p.url)}">${escape(p.title)}</a></li>`);
      const any = products || articles || pages;
      const all = `<a class="header-search__all" href="/search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">${any ? 'View all results' : 'No results. Search anyway'}</a>`;
      return products + articles + pages + all;
    }
  });
}
